import { Injectable, OnInit } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { Observable, throwError, Subscriber, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface StartStopResult {
  connected: boolean;
  info: {
    msg: string,
    data: any
  };
}

@Injectable({
  providedIn: 'root'
})
export class WsNotificationService implements OnInit {

  private connection: HubConnection;
  private events: { evt: string, cb: (data: any) => void }[] = [];

  /**
   * Flag para evitar abrir uma connection mais de uma vez
   */
  private connectionInProgress = false;

  constructor() { }

  ngOnInit(): void { }

  /**
   * Encerra uma conexão com o WebSocket
   */
  public stop(): Observable<StartStopResult> {

    const stopped = !this.connection || this.connection.state === "Disconnected";
    if (stopped) {
      return new Observable<StartStopResult>(obs => {
        obs.next({ connected: false, info: { msg: 'Already Disconnected', data: null } });
      });
    }

    try {
      return new Observable<StartStopResult>((obs: Subscriber<StartStopResult>) => {
        this.connection
          .stop()
          .then(() => {
            obs.next({ connected: false, info: { msg: 'Disconnected', data: null } });
          })
          .catch(err => {
            obs.error({ connected: !stopped, info: { msg: 'Error when disconnecting', data: err } });
          });
      });
    } catch (err) {
      return new Observable<StartStopResult>(obs => {
        obs.error({ connected: !stopped, info: { msg: 'Error when disconnecting', data: err } });
      });
    }
  }

  /**
   * Abre uma conexão com o WebSocket
   * @param token Token de acesso.
   */
  start(token: string): Observable<StartStopResult> {

    if (!token) {
      throw new Error('You cant pass a null token');
    }

    if (this.connection && this.connection.state !== "Disconnected" || this.connectionInProgress) {
      return EMPTY;
    }

    this.connectionInProgress = true;


    // build connection
    this.connection = new HubConnectionBuilder()
      .withUrl(`${environment.serverAddress}/wsh/notifications`, { accessTokenFactory: () => token })
      .withAutomaticReconnect([0, 5000, 10000, 60000, null])
      .configureLogging(LogLevel.Error)
      .build();

    // if we have listeners to register
    if (this.events && this.events.length > 0) {
      (this.events || []).forEach(evt => {
        this.connection.on(evt.evt, data => {
          evt.cb(data);
        });
      });
      this.events = null;
    }

    // connect to the server and converts to observer
    try {
      return new Observable<StartStopResult>(obs => {
        this.connection.start()
          .then(() => {
            this.connectionInProgress = false;
            obs.next({
              connected: true,
              info: { msg: 'Connected', data: null }
            });
          })
          .catch(err => {
            this.connectionInProgress = false;
            obs.error({
              connected: false,
              info: { msg: 'Não foi possível se conectar ao servidor WS, click aqui para tentar novamente', data: err }
            });
          });
      })
        .pipe(catchError(err => {
          return throwError(err);
        }));
    } catch (err) {
      return new Observable<StartStopResult>(obs => {
        obs.error({
          connected: false,
          info: { msg: 'Fail when trying to connect', data: err }
        });
      })
        .pipe(catchError(err => {
          return throwError(err);
        }));
    }
  }

  // TODO: remover callback
  // listen to events
  on(eventName: string, callback: (data: any) => void): void {

    // if we have an instance of a connection
    if (this.connection) {
      this.connection.on(eventName, data => {
        callback(data);
      });

      // else we store it
    } else {
      if (!this.events) {
        this.events = [];
      }
      this.events.push({
        evt: eventName,
        cb: callback
      });
    }
  }
}
